import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useOrganization() {
  return useQuery({
    queryKey: ["organization"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization")
        .select("*")
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: { name?: string; logo_url?: string; settings?: any }) => {
      const { data: org } = await supabase
        .from("organization")
        .select("id")
        .limit(1)
        .single();

      if (!org) throw new Error("Organization not found");

      const { data, error } = await supabase
        .from("organization")
        .update(updates)
        .eq("id", org.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization"] });
      toast({
        title: "Organization updated",
        description: "Organization settings have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating organization",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useOrganizationStats() {
  return useQuery({
    queryKey: ["organization-stats"],
    queryFn: async () => {
      const { count } = await supabase
        .from("user_account")
        .select("*", { count: "exact", head: true })
        .eq("is_active", true);

      return {
        memberCount: count || 0,
      };
    },
  });
}

export type VolumeUnit = "gallons" | "liters" | "barrels";

/**
 * Get the volume unit label for display
 */
export function getVolumeUnitLabel(unit: VolumeUnit): string {
  switch (unit) {
    case "gallons":
      return "Gallons";
    case "liters":
      return "Liters";
    case "barrels":
      return "Barrels";
    default:
      return "Gallons";
  }
}

/**
 * Get the volume unit abbreviation for display
 */
export function getVolumeUnitAbbrev(unit: VolumeUnit): string {
  switch (unit) {
    case "gallons":
      return "gal";
    case "liters":
      return "L";
    case "barrels":
      return "bbl";
    default:
      return "gal";
  }
}

/**
 * Hook to get the organization's preferred volume unit
 * Defaults to "gallons" if not set
 */
export function useVolumeUnit(): { volumeUnit: VolumeUnit; volumeLabel: string; volumeAbbrev: string; isLoading: boolean } {
  const { data: org, isLoading } = useOrganization();
  const settings = (org?.settings as any) || {};
  const volumeUnit: VolumeUnit = settings.volume_unit || "gallons";

  return {
    volumeUnit,
    volumeLabel: getVolumeUnitLabel(volumeUnit),
    volumeAbbrev: getVolumeUnitAbbrev(volumeUnit),
    isLoading,
  };
}
